import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/util/cn';

// The v3 surface card. Bakes in the chrome string
//   'rounded-md border border-border bg-card'
// that ~23 surfaces had been repeating by hand. Renders <section> by
// default; switch to article/div via `as`.
//
// Picking padding by content shape:
//   default — standard content card (snowfall card sidebar, related news, etc).
//             p-5 sm, p-6 md+.
//   sm      — compact card (AtAGlance / SnowfallCard sidebar boxes).
//             p-4 at all sizes.
//   lg      — testimonial / hero-ish card. p-6 sm, p-8 md+.
//   none    — for list/table containers where the table itself paints
//             the internal padding. Add `overflow-hidden` via className.
//
// Use `className` for layout (margins, shadow, overflow-hidden, flex/grid
// in-card layout) — not for re-stating the chrome.

const sectionCardVariants = cva(
  'rounded-md border border-border bg-card',
  {
    variants: {
      padding: {
        default: 'p-5 md:p-6',
        sm: 'p-4',
        lg: 'p-6 md:p-8',
        none: '',
      },
    },
    defaultVariants: { padding: 'default' },
  },
);

type SectionCardTag = 'section' | 'article' | 'div';

export interface SectionCardProps
  extends React.HTMLAttributes<HTMLElement>,
  VariantProps<typeof sectionCardVariants> {
  as?: SectionCardTag;
}

const SectionCard = React.forwardRef<HTMLElement, SectionCardProps>(
  ({
    as: Tag = 'section', padding, className, ...props
  }, ref) => (
    <Tag
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      className={cn(sectionCardVariants({ padding }), className)}
      {...props}
    />
  ),
);
SectionCard.displayName = 'SectionCard';

export { SectionCard, sectionCardVariants };
